import { z } from 'zod';

import { apiClient } from './api-client';

const statusSchema = z.object({
  configured: z.boolean(),
  connected: z.boolean(),
  state: z.enum(['open', 'connecting', 'close', 'unknown']),
  instance: z.string().nullable(),
  phoneNumber: z.string().nullable(),
  profileName: z.string().nullable(),
});

const connectSchema = z.object({
  state: z.enum(['open', 'connecting', 'close', 'unknown']),
  qrCode: z.string().nullable(),
  pairingCode: z.string().nullable(),
});

const disconnectSchema = z.object({ disconnected: z.boolean() });

const setupWebhookSchema = z.object({
  ok: z.boolean(),
  webhookUrl: z.string(),
  events: z.array(z.string()),
  errorMessage: z.string().nullable(),
});

const sendTestSchema = z.object({
  ok: z.boolean(),
  sentTo: z.string(),
  externalMessageId: z.string().nullable(),
  errorMessage: z.string().nullable(),
});

const webhookInfoSchema = z.object({
  registered: z.boolean(),
  enabled: z.boolean(),
  currentUrl: z.string().nullable(),
  expectedUrl: z.string().nullable(),
  pointsToUs: z.boolean(),
  events: z.array(z.string()),
});

export type WebhookInfo = z.infer<typeof webhookInfoSchema>;

export type WhatsappStatus = z.infer<typeof statusSchema>;
export type WhatsappConnectResult = z.infer<typeof connectSchema>;
export type SetupWebhookResult = z.infer<typeof setupWebhookSchema>;

export async function fetchWhatsappStatus(): Promise<WhatsappStatus> {
  const { data } = await apiClient.get('/api/whatsapp/status');
  return statusSchema.parse(data.data);
}

export async function connectWhatsapp(): Promise<WhatsappConnectResult> {
  const { data } = await apiClient.post('/api/whatsapp/connect');
  return connectSchema.parse(data.data);
}

export async function disconnectWhatsapp(): Promise<{ disconnected: boolean }> {
  const { data } = await apiClient.post('/api/whatsapp/disconnect');
  return disconnectSchema.parse(data.data);
}

export async function setupWhatsappWebhook(): Promise<SetupWebhookResult> {
  const { data } = await apiClient.post('/api/whatsapp/setup-webhook');
  return setupWebhookSchema.parse(data.data);
}

export async function fetchWhatsappWebhookInfo(): Promise<WebhookInfo> {
  const { data } = await apiClient.get('/api/whatsapp/webhook');
  return webhookInfoSchema.parse(data.data);
}

export type SendTestResult = z.infer<typeof sendTestSchema>;

export async function sendWhatsappTest(input: {
  phone: string;
  text?: string;
}): Promise<SendTestResult> {
  const { data } = await apiClient.post('/api/whatsapp/send-test', input);
  return sendTestSchema.parse(data.data);
}
